import { Button, PortalProps, Tooltip } from "@fluentui/react-components";
import { memo, MouseEvent, MouseEventHandler } from "react";
import ArrowCircleDownOutlineIcon from "~icons/material-symbols/arrow-circle-down-outline";
import ArrowCircleUpOutlineIcon from "~icons/material-symbols/arrow-circle-up-outline";
import CancelIcon from "~icons/material-symbols/cancel";
import CancelOutlineIcon from "~icons/material-symbols/cancel-outline";
import CheckCircleIcon from "~icons/material-symbols/check-circle";
import CheckCircleOutlineIcon from "~icons/material-symbols/check-circle-outline";
import InfoOutlineIcon from "~icons/material-symbols/info-outline";
import { DEFAULT_APPLICATION_ID } from "../../../constants";
import { useAppData, useContextApp } from "../../../hooks";
import { IClause, IClauseInfoData } from "../../../interfaces";
import { ClauseCardProps } from "../../../types/ClauseCardProps";
import { ApplicationType, ClauseCardMode } from "../../../types/enums";
import { bundleIcon } from "../../icons";
import { useClauseCardSideActionStyles } from "../styles/ClauseCardSideAction.styles";
import { SideActionRenderMenu } from "./SideActionRenderMenu";

const ClauseAcceptedIcon = bundleIcon(CheckCircleIcon, CheckCircleOutlineIcon);
const ClauseDeclinedIcon = bundleIcon(CancelIcon, CancelOutlineIcon);

type ClauseCardSideActionProps = ClauseCardProps &
  Pick<PortalProps, "mountNode"> & {
    expanded: boolean;
    setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
    hasTextOverflow: boolean;
    setHasTextOverflow: React.Dispatch<React.SetStateAction<boolean>>;
  };

export const ClauseCardSideAction = memo(
  ({ expanded, setExpanded, hasTextOverflow, setHasTextOverflow, ...props }: ClauseCardSideActionProps) => {
    const styles = useClauseCardSideActionStyles();
    const { createContextApp } = useContextApp();

    // session-scoped state handled via app-scoped data
    const currentAppId = props.appId || DEFAULT_APPLICATION_ID;
    const [goodSuggestions, setGoodSuggestions] = useAppData<Array<IClause>>(currentAppId, "goodSuggestions", []);
    const [declinedClauses, setDeclinedClauses] = useAppData<Array<string>>(currentAppId, "declinedClauses", []);

    const onClickMoreInfo: MouseEventHandler = (ev: MouseEvent) => {
      ev.preventDefault();
      createContextApp(ApplicationType.CLAUSE_INFO_APP, {
        name: `Info - ${props.clause.clause_id}`,
        clause: props.clause,
        updatedText: props.clause.clause_text,
        selectedTab: "documentInfo",
        workspaceId: props.workspaceId,
        similarClauseRedline: false,
      } as IClauseInfoData);
    };

    const onClickExpand: MouseEventHandler = (ev: MouseEvent) => {
      ev.preventDefault();
      setExpanded(!expanded);
    };

    const liked = goodSuggestions.some((g) => g.clause_id === props.clause.clause_id);

    const handleAccept = (e: MouseEvent) => {
      e.preventDefault();

      if (liked) {
        setGoodSuggestions((prev) => (prev || []).filter((g) => g.clause_id !== props.clause.clause_id));
      } else {
        setGoodSuggestions((prev) => [...(prev || []), props.clause]);
      }
      // if previously declined, remove from declined list
      if (declinedClauses?.includes(props.clause.clause_id)) {
        setDeclinedClauses((prev) => (prev || []).filter((id) => id !== props.clause.clause_id));
      }
    };

    const handleDecline = (e: MouseEvent) => {
      e.preventDefault();
      // remove from good suggestions if present
      if (liked) {
        setGoodSuggestions((prev) => (prev || []).filter((g) => g.clause_id !== props.clause.clause_id));
      }
      // add to declined list if not present
      if (!declinedClauses?.includes(props.clause.clause_id)) {
        setDeclinedClauses((prev) => [...(prev || []), props.clause.clause_id]);
      }
    };

    return (
      <div className={styles.clauseCardSideActionContainer}>
        {props.mode === ClauseCardMode.SUGGESTION && (
          <>
            <Button
              appearance="transparent"
              icon={<ClauseAcceptedIcon color="#1EA82C" filled={liked} />}
              onClick={handleAccept}
              size="small"
            />

            <Button
              appearance="transparent"
              icon={<ClauseDeclinedIcon color="#A40C0C" />}
              onClick={handleDecline}
              size="small"
            />
          </>
        )}

        {props.mode !== ClauseCardMode.DETAIL && (
          <>
            <Tooltip content="More Information" relationship="label" positioning="after">
              <Button
                size="small"
                appearance="transparent"
                icon={<InfoOutlineIcon color="#242424" />}
                onClick={onClickMoreInfo}
              />
            </Tooltip>
            {SideActionRenderMenu(props)}
          </>
        )}

        {(hasTextOverflow || expanded) && (
          <Tooltip content="Expand/Collapse" relationship="label" positioning="after">
            <Button
              className={styles.expandedButton}
              size="small"
              appearance="transparent"
              icon={
                expanded ? <ArrowCircleUpOutlineIcon color="#2F80ED" /> : <ArrowCircleDownOutlineIcon color="#2F80ED" />
              }
              onClick={onClickExpand}
            />
          </Tooltip>
        )}
      </div>
    );
  }
);
